import type { MaybeRefOrGetter, Ref } from 'vue'
import { useScrollLock } from '@vueuse/core'

/** 单例：多处调用共用同一把 body 锁，避免互相覆盖 */
let bodyLocked: Ref<boolean> | null = null

function getBodyLocked() {
  if (!import.meta.client) return null
  if (!bodyLocked) {
    bodyLocked = useScrollLock(document.body)
  }
  return bodyLocked
}

/**
 * 全局 body 滚动锁定（引用计数，多弹层可叠用）
 *
 * @example
 * const { lockWhile } = useBodyScrollLock()
 * lockWhile(open) // open 为 true 时锁定，false / 卸载时解锁
 *
 * @example
 * const { lock, unlock } = useBodyScrollLock()
 * lock()
 * // ...
 * unlock()
 */
export function useBodyScrollLock() {
  const count = useState('body-scroll-lock-count', () => 0)
  const body = getBodyLocked()

  const sync = () => {
    if (body) body.value = count.value > 0
  }

  const lock = () => {
    count.value += 1
    sync()
  }

  const unlock = () => {
    if (count.value <= 0) return
    count.value -= 1
    sync()
  }

  /** 跟随 boolean / Ref / getter：为 true 时加锁，变 false 或组件卸载时解锁 */
  const lockWhile = (source: MaybeRefOrGetter<boolean>) => {
    watch(
      () => toValue(source),
      (v, was) => {
        if (v) lock()
        else if (was) unlock()
      },
      { immediate: true },
    )

    onBeforeUnmount(() => {
      if (toValue(source)) unlock()
    })
  }

  return {
    lock,
    unlock,
    lockWhile,
    isLocked: computed(() => count.value > 0),
  }
}
